import { dateFormats, swaDate } from "@swa-ui/date";
import { createHrefWithSearchParams } from "@swa-ui/href/createHrefWithSearchParams";

const CARTRAWLER_DATE_FORMAT = `${dateFormats.iso}T${dateFormats.twentyFourHourTime}`;

/**
 * @typedef {Object} CarTrawlerDeeplinkLocation
 * @property {String} iataCode 3-letter airport IATA code (e.g., "DAL").
 * @property {Date|String} dateTime Date/time for the leg. `Date` instances and ISO strings are accepted.
 */

/**
 * @typedef {Object} CarTrawlerDeeplinkParams
 * @property {String} baseUrl CarTrawler booking engine base URL (e.g., "https://cars.cartrawler.com/partner/en/book").
 * @property {String} clientID CarTrawler client identifier.
 * @property {CarTrawlerDeeplinkLocation} pickupLocation Pickup location and date/time.
 * @property {CarTrawlerDeeplinkLocation} [returnLocation] Return location and date/time. Defaults to `pickupLocation` when omitted.
 * @property {String} [currency] ISO 4217 currency code (e.g., "USD").
 * @property {String} [language] Language code (e.g., "en").
 */

/**
 * Formats a Date or ISO string into CarTrawler short format: `YYYY-MM-DDThh:mm`.
 *
 * @param {Date|String} dateTime
 * @returns {String|null} Formatted string, or null when the input cannot be parsed.
 */
const formatDateTime = (dateTime) => {
  const parsed = dateTime != null ? swaDate(dateTime) : null;

  return parsed?.isValid() ? parsed.format(CARTRAWLER_DATE_FORMAT) : null;
};

/**
 * Builds a CarTrawler booking engine deeplink URL using IATA airport codes.
 *
 * Given a pickup (and optional return) location plus date/time, returns a Step 2 deeplink that
 * sends the user directly into search results. When the returnLocation is omitted it defaults to
 * the pickup location. Currently only the IATA-code location strategy is supported; other
 * strategies documented by CarTrawler (Location ID, name, coordinates) can be added later.
 * Locations that lack an IATA code, or date/times that cannot be parsed, yield null.
 *
 * @param {CarTrawlerDeeplinkParams} deeplinkParams
 * @returns {String|null} The full deeplink URL, or null when required inputs are missing or invalid.
 */
export const generateCarTrawlerDeeplink = (deeplinkParams) => {
  const { baseUrl, clientID, currency, language, pickupLocation, returnLocation } =
    deeplinkParams || {};
  const effectiveReturn = returnLocation || pickupLocation;
  const pickupDateTime = formatDateTime(pickupLocation?.dateTime);
  const returnDateTime = formatDateTime(effectiveReturn?.dateTime);
  const isValid =
    !!baseUrl &&
    !!pickupLocation?.iataCode &&
    !!effectiveReturn?.iataCode &&
    !!pickupDateTime &&
    !!returnDateTime;

  const { href = null } = isValid
    ? createHrefWithSearchParams(baseUrl, {
        ...(clientID && { clientID }),
        ...(currency && { currency }),
        ...(language && { language }),
        pickupDateTime,
        pickupIATACode: pickupLocation.iataCode,
        returnDateTime,
        returnIATACode: effectiveReturn.iataCode,
      })
    : {};

  return href;
};
